/**
 * Client-side email preview builder for automation action nodes.
 * Mirrors the backend EmailTemplatesService.buildHtml to show accurate previews.
 */

interface PreviewProduct {
  title: string;
  thumbnailPath?: string;
  link?: string;
}

interface PreviewOptions {
  headerTitle?: string;
  headerSubtitle?: string;
  welcomeMessage?: string;
  productSectionTitle?: string;
  ctaButtonText?: string;
  footerText?: string;
  companyName?: string;
  companyLogo?: string;
  primaryColor?: string;
  products?: PreviewProduct[];
  productLayout?: string;
  isAI?: boolean;
}

function renderProductCards(products: PreviewProduct[], primaryColor: string, ctaText: string, layout: string): string {
  const placeholderBg = '#f3f4f6';

  if (layout === 'minimal-list') {
    return products.map(p => `
      <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:12px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
        <tr>
          <td width="80" style="padding:0;background:${placeholderBg};">
            ${p.thumbnailPath ? `<img src="${p.thumbnailPath}" width="80" height="80" style="display:block;object-fit:cover;" />` : `<div style="width:80px;height:80px;background:${placeholderBg};"></div>`}
          </td>
          <td style="padding:12px 16px;vertical-align:middle;">
            <div style="color:#111827;font-size:14px;font-weight:600;margin:0 0 4px;">${p.title}</div>
            <a href="${p.link || '#'}" style="color:${primaryColor};font-size:13px;text-decoration:none;font-weight:500;">${ctaText} &rarr;</a>
          </td>
        </tr>
      </table>
    `).join('');
  }

  if (layout === 'featured-hero') {
    return products.map((p, i) => `
      <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:12px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
        <tr><td style="background:${placeholderBg};height:${i === 0 ? '180' : '100'}px;text-align:center;vertical-align:middle;">
          ${p.thumbnailPath ? `<img src="${p.thumbnailPath}" width="100%" height="${i === 0 ? 180 : 100}" style="display:block;object-fit:cover;" />` : `<div style="width:64px;height:64px;border-radius:8px;background:#d1d5db;margin:0 auto;"></div>`}
        </td></tr>
        <tr><td style="padding:14px;">
          <div style="color:#111827;font-size:${i === 0 ? '16' : '13'}px;font-weight:600;margin:0 0 8px;">${p.title}</div>
          <a href="${p.link || '#'}" style="display:inline-block;background:${primaryColor};color:#fff;font-size:12px;font-weight:500;text-decoration:none;padding:7px 16px;border-radius:6px;">${ctaText}</a>
        </td></tr>
      </table>
    `).join('');
  }

  if (layout === 'compact-tiles') {
    let html = '<table width="100%" cellpadding="0" cellspacing="0"><tr>';
    products.forEach((p, i) => {
      if (i > 0 && i % 3 === 0) html += '</tr><tr>';
      html += `
        <td width="33%" style="padding:4px;vertical-align:top;">
          <div style="border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;text-align:center;">
            <div style="background:${placeholderBg};height:80px;"></div>
            <div style="padding:8px;font-size:11px;font-weight:600;color:#111827;">${p.title}</div>
          </div>
        </td>`;
    });
    while (products.length % 3 !== 0) { html += '<td width="33%" style="padding:4px;"></td>'; products.push({ title: '' }); }
    html += '</tr></table>';
    return html;
  }

  // Default: grid-cards (2 columns)
  let html = '<table width="100%" cellpadding="0" cellspacing="0"><tr>';
  products.forEach((p, i) => {
    if (i > 0 && i % 2 === 0) html += '</tr><tr>';
    html += `
      <td width="50%" style="padding:6px;vertical-align:top;">
        <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
          <tr><td style="background:${placeholderBg};height:120px;text-align:center;vertical-align:middle;">
            ${p.thumbnailPath ? `<img src="${p.thumbnailPath}" width="100%" height="120" style="display:block;object-fit:cover;" />` : `<div style="width:48px;height:48px;border-radius:8px;background:#d1d5db;margin:0 auto;"></div>`}
          </td></tr>
          <tr><td style="padding:12px;">
            <div style="color:#111827;font-size:13px;font-weight:600;margin:0 0 8px;">${p.title}</div>
            <a href="${p.link || '#'}" style="display:inline-block;background:${primaryColor};color:#fff;font-size:12px;font-weight:500;text-decoration:none;padding:6px 14px;border-radius:6px;">${ctaText}</a>
          </td></tr>
        </table>
      </td>`;
  });
  if (products.length % 2 !== 0) html += '<td width="50%" style="padding:6px;"></td>';
  html += '</tr></table>';
  return html;
}

const AI_BADGE = '<span style="display:inline-block;background:linear-gradient(135deg,rgba(168,85,247,0.12),rgba(74,222,128,0.10));border:1px solid rgba(168,85,247,0.18);border-radius:4px;padding:2px 8px;font-size:10px;font-weight:500;color:#7C3AED;letter-spacing:0.3px;margin-left:6px;vertical-align:middle;">AI</span>';

const aiPlaceholder = (text: string) => `<span style="color:#9ca3af;font-style:italic;">${text}</span>${AI_BADGE}`;

const SAMPLE_PRODUCTS: PreviewProduct[] = [
  { title: 'Sample Product 1', thumbnailPath: '', link: '#' },
  { title: 'Sample Product 2', thumbnailPath: '', link: '#' },
  { title: 'Sample Product 3', thumbnailPath: '', link: '#' },
  { title: 'Sample Product 4', thumbnailPath: '', link: '#' },
];

/**
 * Build preview from an actual template HTML (from EmailTemplatesService) with placeholder replacement.
 */
export function buildPreviewFromTemplate(templateHtml: string, config: any): string {
  let html = templateHtml;
  const isAI = config.useAI !== false;

  // Replace placeholders with config values or AI badges
  html = html.replace(/\{\{COMPANY_NAME\}\}/g, config.companyName || 'Your Company');
  html = html.replace(/\{\{COMPANY_LOGO\}\}/g, config.companyLogo
    ? `<img src="${config.companyLogo}" alt="Logo" style="max-height:60px;max-width:200px;height:auto;" />`
    : `<div style="background:rgba(255,255,255,0.2);color:#fff;padding:12px 24px;border-radius:8px;font-size:18px;font-weight:bold;display:inline-block;">${(config.companyName || 'Co').charAt(0)}</div>`
  );
  html = html.replace(/\{\{HEADER_TITLE\}\}/g, config.headerTitle || (isAI ? aiPlaceholder('Personalized headline') : 'Exclusive Offers For You'));
  html = html.replace(/\{\{HEADER_SUBTITLE\}\}/g, config.headerSubtitle || (isAI ? aiPlaceholder('Tailored subtitle') : ''));
  html = html.replace(/\{\{WELCOME_MESSAGE\}\}/g, config.welcomeMessage || (isAI ? aiPlaceholder('AI-written message based on lead activity') : ''));
  html = html.replace(/\{\{PRODUCT_SECTION_TITLE\}\}/g, config.productSectionTitle || (isAI ? aiPlaceholder('Product section title') : 'Featured Products'));
  html = html.replace(/\{\{FOOTER_TEXT\}\}/g, config.footerText || (isAI ? aiPlaceholder('Personalized footer') : 'Thank you!'));
  html = html.replace(/\{\{COPYRIGHT_TEXT\}\}/g, `© ${new Date().getFullYear()} ${config.companyName || 'Your Company'}`);
  html = html.replace(/\{\{PRIMARY_COLOR\}\}/g, config.primaryColor || '#111827');
  html = html.replace(/\{\{SECONDARY_COLOR\}\}/g, config.secondaryColor || '#374151');
  html = html.replace(/\{\{CTA_BUTTON_TEXT\}\}/g, config.ctaButtonText || 'View Product →');
  html = html.replace(/\{\{POWERED_BY_TEXT\}\}/g, 'Powered by');
  html = html.replace(/\{\{POWERED_BY_ARDEALY_TEXT\}\}/g, 'Ardealy');

  // Products placeholder — render sample product cards using selected layout
  const sampleProducts: PreviewProduct[] = [
    { title: 'Sample Product 1' },
    { title: 'Sample Product 2' },
    { title: 'Sample Product 3' },
    { title: 'Sample Product 4' },
  ];
  const color = config.primaryColor || '#111827';
  const cta = config.ctaButtonText || 'View Product →';
  const layout = config.productLayout || 'grid-cards';
  const sampleProductsHtml = renderProductCards(sampleProducts, color, cta, layout);
  html = html.replace(/\{\{PRODUCTS\}\}/g, sampleProductsHtml);

  // Voucher section — remove or show placeholder
  html = html.replace(/\{\{VOUCHER_SECTION\}\}/g, '');

  return html;
}

export function buildEmailPreviewHtml(opts: PreviewOptions): string {
  const isAI = opts.isAI !== false;
  const {
    headerTitle = isAI ? aiPlaceholder('Personalized headline') : 'Exclusive Offers For You',
    headerSubtitle = isAI ? aiPlaceholder('Tailored subtitle') : '',
    welcomeMessage = isAI ? aiPlaceholder('AI-written welcome message based on lead activity') : '',
    productSectionTitle = isAI ? aiPlaceholder('Product section title') : 'Featured Products',
    ctaButtonText = 'View Product',
    footerText = isAI ? aiPlaceholder('Personalized footer') : 'Thank you for being a valued customer!',
    companyName = 'Your Company',
    companyLogo = '',
    primaryColor = '#111827',
    products = SAMPLE_PRODUCTS,
    productLayout = 'grid-cards',
  } = opts;

  const logoHtml = companyLogo
    ? `<img src="${companyLogo}" alt="${companyName}" style="max-height:50px;max-width:180px;height:auto;width:auto;" />`
    : companyName
      ? `<div style="color:#fff;font-size:18px;font-weight:700;letter-spacing:-0.5px;">${companyName}</div>`
      : '';

  const renderProducts = () => {
    if (products.length === 0) return '';
    return renderProductCards(products, primaryColor, ctaButtonText, productLayout);
  };

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>body{margin:0;padding:0;background:#f4f5f7;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}</style>
</head>
<body>
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f5f7;padding:32px 16px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,0.06);">
          <tr>
            <td style="background:${primaryColor};padding:28px 40px;text-align:center;">
              ${logoHtml ? `<div style="margin-bottom:16px;">${logoHtml}</div>` : ''}
              <h1 style="color:#fff;font-size:22px;font-weight:600;margin:0 0 6px;line-height:1.3;">${headerTitle}</h1>
              ${headerSubtitle ? `<p style="color:rgba(255,255,255,0.75);font-size:14px;margin:0;line-height:1.4;">${headerSubtitle}</p>` : ''}
            </td>
          </tr>
          ${welcomeMessage ? `<tr><td style="padding:28px 40px 16px;"><p style="color:#374151;font-size:15px;line-height:1.6;margin:0;white-space:pre-line;">${welcomeMessage}</p></td></tr>` : ''}
          <tr>
            <td style="padding:0 40px 8px;">
              <h2 style="color:#111827;font-size:16px;font-weight:600;margin:0 0 16px;">${productSectionTitle}</h2>
              ${renderProducts()}
            </td>
          </tr>
          <tr>
            <td style="padding:24px 40px;border-top:1px solid #e5e7eb;text-align:center;">
              <p style="color:#9ca3af;font-size:13px;margin:0 0 4px;line-height:1.5;">${footerText}</p>
              <p style="color:#d1d5db;font-size:12px;margin:0;">&copy; ${new Date().getFullYear()} ${companyName}</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}
